import { NAMED_THEMES, themeSchemes, type ThemeName } from '@church/ui-tokens'
import { useState } from 'react'

/**
 * Light, dark, whatever the device is set to, or one of the named themes
 * (Graphite, Ember…). The choice lives in this browser (`forhim.theme`);
 * `public/theme.js` applies it before the first paint, so a dark page never
 * flashes white. The tokens switch on `<html>`: `.dark` for every theme that
 * is dark underneath (so `dark:` styles and native controls follow), and
 * `data-theme` for a named one's own colours.
 */
export type ThemeChoice = ThemeName | 'system'

const KEY = 'forhim.theme'
const systemDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches
const isTheme = (value: string | null): value is ThemeName =>
  value === 'light' || value === 'dark' || (NAMED_THEMES as readonly string[]).includes(value ?? '')

export function readTheme(): ThemeChoice {
  try {
    const saved = localStorage.getItem(KEY)
    if (isTheme(saved)) return saved
  } catch {
    // Storage blocked: the device decides.
  }
  return 'system'
}

function apply(choice: ThemeChoice) {
  const root = document.documentElement
  const dark = choice === 'system' ? systemDark() : themeSchemes[choice] === 'dark'
  root.classList.toggle('dark', dark)
  if (choice === 'system' || choice === 'light' || choice === 'dark') delete root.dataset.theme
  else root.dataset.theme = choice
}

/** Applies the choice and follows the device's setting while it is System. Once, at boot. */
export function followSystemTheme() {
  apply(readTheme())
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => apply(readTheme()))
}

export function useTheme() {
  const [choice, setChoice] = useState(readTheme)
  const choose = (next: ThemeChoice) => {
    setChoice(next)
    try {
      if (next === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, next)
    } catch {
      // Applies for this visit only.
    }
    apply(next)
  }
  return { choice, choose }
}
